import { useCallback, useMemo, useRef } from "react";
import { uniqueId, useEditor } from "tldraw";
import { TldrawAiModule } from "./TldrawAiModule.mjs";
function useTldrawAi(opts) {
  const { editor: _editor, generate: generateFn, stream: streamFn, transforms } = opts;
  const editor = _editor ?? useEditor();
  const ai = useMemo(() => new TldrawAiModule({ editor, transforms }), [editor, transforms]);
  const rCancelFunction = useRef(null);
  const rPreviousArguments = useRef("");
  const rPreviousChanges = useRef([]);
  const prompt = useCallback(
    (message) => {
      let cancelled = false;
      const controller = new AbortController();
      const signal = controller.signal;
      const opts2 = typeof message === "string" ? { message } : message;
      const { stream = false } = opts2;
      const markId = "generating_" + uniqueId();
      const promise = new Promise(async (resolve, reject) => {
        if (!ai) {
          reject();
          return;
        }
        const { prompt: prompt2, handleChange } = await ai.generate(message);
        const serializedPrompt = {
          ...prompt2,
          promptBounds: prompt2.promptBounds.toJson(),
          contextBounds: prompt2.contextBounds.toJson()
        };
        const pendingChanges = [];
        if (stream) {
          if (!streamFn) {
            throw Error(
              `Stream function not found. You should pass a generate method in your call to the useTldrawAi hook.`
            );
          }
          editor.markHistoryStoppingPoint(markId);
          for await (const change of streamFn({
            editor,
            prompt: serializedPrompt,
            signal
          })) {
            if (!cancelled) {
              try {
                editor.run(
                  () => {
                    handleChange(change);
                  },
                  {
                    ignoreShapeLock: false,
                    // ? should this be true?
                    history: "record"
                    // ? should this be 'ignore'?
                  }
                );
                pendingChanges.push(change);
              } catch (e) {
                editor.bailToMark(markId);
                throw e;
              }
            }
          }
        } else {
          if (!generateFn) {
            throw Error(
              `Stream function not found. You should pass a stream method in your call to the useTldrawAi hook.`
            );
          }
          const changes = await generateFn({ editor, prompt: serializedPrompt, signal }).catch(
            (error) => {
              if (error.name === "AbortError") {
                console.error("Cancelled");
              } else {
                console.error("Fetch error:", error);
              }
            }
          );
          if (changes && !cancelled) {
            editor.markHistoryStoppingPoint(markId);
            try {
              editor.run(
                () => {
                  for (const change of changes) {
                    pendingChanges.push(change);
                    handleChange(change);
                  }
                },
                {
                  ignoreShapeLock: false,
                  // ? should this be true?
                  history: "record"
                  // ? should this be 'ignore'?
                }
              );
            } catch (e) {
              editor.bailToMark(markId);
              throw e;
            }
          }
        }
        rPreviousArguments.current = opts2;
        rPreviousChanges.current = pendingChanges;
        rCancelFunction.current = null;
        resolve();
      });
      rCancelFunction.current = () => {
        cancelled = true;
        controller.abort("Cancelled by user");
        editor.bailToMark(markId);
        rCancelFunction.current = null;
      };
      return {
        // the promise that will resolve the changes
        promise,
        // a helper to cancel the request
        cancel: rCancelFunction.current
      };
    },
    [ai, generateFn, streamFn]
  );
  const repeat = useCallback(() => {
    const promise = new Promise(async (resolve, reject) => {
      if (!ai) {
        reject();
        return;
      }
      const prevOpts = rPreviousArguments.current;
      const { handleChange } = await ai.generate(prevOpts);
      editor.run(
        () => {
          for (const change of rPreviousChanges.current) {
            handleChange(change);
          }
        },
        {
          ignoreShapeLock: false,
          // should this be true?
          history: "record"
          // should this be 'ignore'?
        }
      );
      rCancelFunction.current = null;
      resolve();
    });
    return {
      promise,
      cancel: rCancelFunction.current
    };
  }, [ai]);
  const cancel = useCallback(() => {
    rCancelFunction.current?.();
  }, []);
  return { prompt, repeat, cancel };
}
export {
  useTldrawAi
};
//# sourceMappingURL=useTldrawAi.mjs.map
